/**
 * Chunked Upload Service
 *
 * Handles large file uploads via the block-based chunked upload API.
 * Slices files into 4MB blocks, stages them sequentially, then commits.
 */
import { documents, templates } from './signApi';

const BLOCK_SIZE = 4 * 1024 * 1024; // 4MB per block (well under 10MB API limit)

/**
 * Core chunked upload logic shared by packages and templates.
 * @param {Object} apiMethods - The chunkedUpload API object (e.g. documents.chunkedUpload or templates.chunkedUpload)
 * @param {string} entityId - Package or template GUID
 * @param {File} file - File to upload
 * @param {Object} [options] - Upload options
 * @returns {Promise<Object>} The committed document data
 */
async function _uploadChunked(apiMethods, entityId, file, options = {}) {
    const totalBlocks = Math.ceil(file.size / BLOCK_SIZE);

    console.log(`[UPLOAD] Starting chunked upload: file=${file.name}, size=${file.size}, blocks=${totalBlocks}, entity=${entityId}`);

    // 1. Initiate upload session
    const initResponse = await apiMethods.initiate(entityId, file.name, file.size);
    const sessionId = initResponse.data.sessionGuid;
    console.log(`[UPLOAD] Session initiated: sessionId=${sessionId}, expires=${initResponse.data.expiresOn}`);

    try {
        // 2. Stage blocks sequentially
        for (let i = 0; i < totalBlocks; i++) {
            if (options.signal?.aborted) {
                throw new DOMException('Upload aborted', 'AbortError');
            }

            const start = i * BLOCK_SIZE;
            const end = Math.min(start + BLOCK_SIZE, file.size);
            const block = file.slice(start, end);

            const t0 = performance.now();
            console.log(`[UPLOAD] Staging block ${i}/${totalBlocks - 1} (${end - start} bytes) session=${sessionId}`);

            try {
                await apiMethods.stageBlock(entityId, sessionId, i, block);
                console.log(`[UPLOAD] Block ${i} staged in ${Math.round(performance.now() - t0)}ms`);
            } catch (blockErr) {
                console.error(`[UPLOAD] Block ${i} FAILED after ${Math.round(performance.now() - t0)}ms:`, blockErr.message, blockErr.response?.status, blockErr.response?.data);
                throw blockErr;
            }

            options.onProgress?.({
                percent: Math.round(((i + 1) / totalBlocks) * 100),
                bytesUploaded: end,
                totalBytes: file.size
            });
        }

        // 3. Commit - assembles blocks into a document
        console.log(`[UPLOAD] All blocks staged, committing session=${sessionId}`);
        const t1 = performance.now();
        const commitResponse = await apiMethods.commit(
            entityId, sessionId, {
                displayName: options.displayName,
                displayOrder: options.displayOrder
            }
        );
        console.log(`[UPLOAD] Commit succeeded in ${Math.round(performance.now() - t1)}ms:`, JSON.stringify(commitResponse.data).substring(0, 200));
        return commitResponse.data;

    } catch (err) {
        console.error(`[UPLOAD] Upload failed for session=${sessionId}:`, err.message, err.response?.status, err.response?.data);
        // Only abort the session on explicit user cancellation.
        if (err.name === 'AbortError' || options.signal?.aborted) {
            console.log(`[UPLOAD] Aborting session=${sessionId} (user cancellation)`);
            apiMethods.abort(entityId, sessionId).catch(() => {});
        }
        throw err;
    }
}

/**
 * Upload a file to a package using the chunked upload API
 * @param {string} packageId - Package GUID
 * @param {File} file - File to upload
 * @param {Object} [options] - Upload options
 * @param {string} [options.displayName] - Document display name
 * @param {number} [options.displayOrder] - Document display order
 * @param {Function} [options.onProgress] - Progress callback: ({ percent, bytesUploaded, totalBytes }) => void
 * @param {AbortSignal} [options.signal] - AbortController signal for cancellation
 * @returns {Promise<Object>} The committed document data
 */
export async function uploadFileChunked(packageId, file, options = {}) {
    return _uploadChunked(documents.chunkedUpload, packageId, file, options);
}

/**
 * Upload a file to a template using the chunked upload API
 * @param {string} templateId - Template GUID
 * @param {File} file - File to upload
 * @param {Object} [options] - Upload options
 * @param {string} [options.displayName] - Document display name
 * @param {number} [options.displayOrder] - Document display order
 * @param {Function} [options.onProgress] - Progress callback: ({ percent, bytesUploaded, totalBytes }) => void
 * @param {AbortSignal} [options.signal] - AbortController signal for cancellation
 * @returns {Promise<Object>} The committed document data
 */
export async function uploadTemplateFileChunked(templateId, file, options = {}) {
    return _uploadChunked(templates.chunkedUpload, templateId, file, options);
}
